import { Component, type ReactNode, type ErrorInfo } from 'react';

// Vite injects `import.meta.env.DEV` at build time. ui-components doesn't depend
// on vite's client types directly, so narrow access via a typed cast.
const IS_DEV = (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV ?? false;

interface Props {
  nodeId: string;
  nodeType: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary for a single surface node. A render-time throw in one
 * component shows a localized fallback in place of that node only; siblings
 * continue to render. Surface-level ContentErrorBoundary remains as a final
 * safety net for anything that escapes this boundary.
 *
 * React 19 still requires class components for error boundaries.
 */
export class NodeErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface via console for dev tools and Sentry-style integrations.
    // Avoids tying ui-components to a specific logger.
    console.error(
      '[NodeErrorBoundary] node threw during render:',
      { nodeId: this.props.nodeId, nodeType: this.props.nodeType },
      error,
      info.componentStack
    );
  }

  override render() {
    if (this.state.hasError) {
      const message = this.state.error?.message ?? 'unknown error';
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            minHeight: '120px',
            border: '1px dashed var(--color-border, #e2e8f0)',
            borderRadius: '0.5rem',
            color: 'var(--color-muted, #64748b)',
            fontSize: '0.875rem',
            textAlign: 'center',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
            This {this.props.nodeType} couldn&apos;t render
          </div>
          {IS_DEV && (
            <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem' }}>{message}</div>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
